import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Typography, Link, Box } from '@mui/material';
import CodeBlock from './CodeBlock';

/**
 * Renders markdown content from AI responses.
 * Handles headers, lists, code blocks, inline code, links, blockquotes.
 * Code blocks always use dark theme with syntax highlighting.
 */
const MarkdownMessage = ({ content }) => {
  if (!content) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Code blocks and inline code
        code({ _node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          const codeString = String(children).replace(/\n$/, '');

          // Render as code block if: has language tag, or is multi-line and not inline
          if (!inline && (match || codeString.includes('\n'))) {
            return <CodeBlock language={match?.[1]} value={codeString} />;
          }

          return (
            <code
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                padding: '2px 6px',
                borderRadius: 4,
                fontFamily: 'monospace',
                fontSize: '0.875em',
              }}
              {...props}
            >
              {children}
            </code>
          );
        },

        // Headers
        h1: ({ children }) => (
          <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>{children}</Typography>
        ),
        h2: ({ children }) => (
          <Typography variant="h6" sx={{ mt: 1.5, mb: 0.75, fontWeight: 600 }}>{children}</Typography>
        ),
        h3: ({ children }) => (
          <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>{children}</Typography>
        ),
        h4: ({ children }) => (
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>{children}</Typography>
        ),

        // Paragraphs
        p: ({ children }) => (
          <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>{children}</Typography>
        ),

        // Links
        a: ({ href, children }) => (
          <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
            {children}
          </Link>
        ),

        // Lists
        ul: ({ children }) => (
          <Box component="ul" sx={{ pl: 2.5, mb: 1, '& li': { mb: 0.25 } }}>{children}</Box>
        ),
        ol: ({ children }) => (
          <Box component="ol" sx={{ pl: 2.5, mb: 1, '& li': { mb: 0.25 } }}>{children}</Box>
        ),
        li: ({ children }) => (
          <Typography component="li" variant="body2" sx={{ lineHeight: 1.6 }}>{children}</Typography>
        ),

        // Blockquotes
        blockquote: ({ children }) => (
          <Box
            sx={{
              borderLeft: '3px solid',
              borderColor: 'primary.light',
              pl: 2,
              py: 0.5,
              my: 1,
              color: 'text.secondary',
              fontStyle: 'italic',
            }}
          >
            {children}
          </Box>
        ),

        // Horizontal rules
        hr: () => (
          <Box sx={{ my: 2, borderBottom: '1px solid', borderColor: 'divider' }} />
        ),

        // Strong/emphasis
        strong: ({ children }) => (
          <strong>{children}</strong>
        ),
        em: ({ children }) => (
          <em>{children}</em>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownMessage;
